import React, { useState } from 'react'
import './ImageCardStyle.css'
import { Dropdown, Form } from 'react-bootstrap'
import { format } from 'date-fns'
import ConfirmBlockModal from '@components/layout/Modals/ConfirmBlockModal/ConfirmBlockModal.component'

type ImageCardProps = {
  imageUrl: string // URL of the image
  fileName: string // File name
  fileSize: string // File size in MB or appropriate format
  uploadDate: string // Date and time string
  isSelected: boolean // Whether the checkbox is selected
  isFavorite: boolean // Whether the favorite is selected
  onSelect: () => void // Function to handle checkbox select
  onFavorite: () => void // Function to handle checkbox select
  onDelete: () => void // Function to handle delete
  onDownload: () => void // Function to handle download
}

// Type definition for the CustomToggle props
interface CustomToggleProps {
  children: React.ReactNode
  onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
}

// The forwardRef is important!!
// Dropdown needs access to the DOM node in order to position the Menu
// eslint-disable-next-line react/display-name
const CustomToggle = React.forwardRef<HTMLAnchorElement, CustomToggleProps>(
  ({ children, onClick }, ref) => (
    <a
      //   href="#"
      ref={ref}
      onClick={(e) => {
        e.preventDefault()
        onClick(e)
      }}
    >
      {children}
    </a>
  ),
)

const ImageCard: React.FC<ImageCardProps> = ({
  imageUrl,
  fileName,
  fileSize,
  uploadDate,
  isSelected,
  isFavorite,
  onSelect,
  onFavorite,
  onDelete,
  onDownload,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  // Safely format date - handle invalid dates gracefully
  const formattedDate = (() => {
    if (!uploadDate) return 'No date available'
    try {
      const date = new Date(uploadDate)
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date'
      }
      return format(date, 'MMM d, yyyy - h:mm a')
    } catch (error) {
      console.warn('Error formatting date:', uploadDate, error)
      return 'Invalid date'
    }
  })()

  const handleDeleteClick = (e: React.MouseEvent): void => {
    e.stopPropagation()
    setShowDeleteModal(true)
  }

  const confirmDelete = (): void => {
    onDelete()
    setShowDeleteModal(false)
  }

  return (
    <>
      <div className="si-image-card-container" onClick={onSelect}>
        <div className="si-image-container">
          <Form.Check
            type="checkbox"
            checked={isSelected}
            onSelect={(e) => {
              e.stopPropagation()
              onSelect()
            }}
            className="si-image-container-checkbox"
          />
          <div className="si-image-holder">
            <img src={imageUrl} alt={fileName} />
          </div>
          <button
            className="star"
            onClick={(e) => {
              e.stopPropagation()
              onFavorite()
            }}
          >
            <i className={`ti ${isFavorite ? 'ti-star-filled' : 'ti-star'}`} />
          </button>
        </div>

        <div className="si-bottom-container">
          <div className="si-bottom-text-container">
            <div className="si-image-title">
              <span className="si-file-name si-ellipsis">{fileName}</span>{' '}
              <span className="si-file-size">({fileSize})</span>
            </div>
            <h5 className="si-image-date">{formattedDate}</h5>
          </div>

          <Dropdown className="si-image-dropdown">
            <Dropdown.Toggle as={CustomToggle}>
              <i className="bi bi-three-dots" style={{ color: '#8A8E97' }} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={(e) => {
                  e.stopPropagation()
                  onDownload()
                }}
              >
                Download
              </Dropdown.Item>
              <Dropdown.Item onClick={handleDeleteClick}>Delete</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <ConfirmBlockModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        mainText="Delete Study?"
        subText={`The study will be deleted from this folder. This action cannot be undone.`}
        action="Delete"
        onConfirm={confirmDelete}
      />
    </>
  )
}

export default ImageCard
